import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Space, Button, DatePicker, Modal, message, Input, Form, Tooltip } from 'antd';
import { ReloadOutlined, UserOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import dayjs from 'dayjs';
import AttachmentUpload from '../components/AttachmentUpload';
import ImageViewer from '../components/ImageViewer';
import { useAuth } from '../contexts/AuthContext';

// 状态颜色映射
const getStatusColor = (status) => {
  const colorMap = {
    'pending': 'blue',
    'approved': 'green', 
    'rejected': 'red',
    'closed': 'gray'
  };
  return colorMap[status] || 'default';
};

// 状态文本映射
const getStatusText = (status) => {
  const textMap = {
    'pending': '待审批',
    'approved': '已通过',
    'rejected': '已拒绝',
    'closed': '已结束'
  };
  return textMap[status] || status;
};

// 判断是否为图片文件
const isImageFile = (file) => {
  if (file.mimetype && file.mimetype.startsWith('image/')) return true;
  if (file.isImage) return true;
  if (file.url && /\.(png|jpe?g|gif|webp|bmp)$/i.test(file.url)) return true;
  return false;
};

// 解析附件 JSON
const parseAttachments = (data) => {
  if (!data || data === 'null' || data === '[]') return [];
  if (typeof data === 'string') return JSON.parse(data);
  if (Array.isArray(data)) return data;
  return [];
};

const Resignations = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && (user.role === 'super_admin' || user.role === 'hr_manager');
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [modal, setModal] = useState({ open: false, record: null, lastDay: null });
  const [closureModal, setClosureModal] = useState({ open: false, record: null });
  const [closureForm] = Form.useForm();
  const [closureAttachments, setClosureAttachments] = useState([]);
  const [detailModal, setDetailModal] = useState({ open: false, record: null });
  // 编辑/重新提交弹窗
  const [editModal, setEditModal] = useState({ open: false, record: null });
  const [editForm] = Form.useForm();
  const [editReason, setEditReason] = useState('');
  const [editLastDay, setEditLastDay] = useState(null);
  const [editAttachments, setEditAttachments] = useState([]);
  // 图片预览
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState({ url: '', name: '', downloadUrl: '' });

  const openImageViewer = (url, name) => {
    setCurrentImage({ url, name: name || '图片', downloadUrl: url });
    setImageViewerVisible(true);
  };

  const closeImageViewer = () => {
    setImageViewerVisible(false);
    setCurrentImage({ url: '', name: '', downloadUrl: '' });
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/resignations');
      if (res.data?.success) setTickets(res.data.data || []);
    } catch (e) {
      message.error('加载离职工单失败');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // 重新提交（驳回后编辑并提交）
  const handleResubmit = async () => {
    const { record } = editModal;
    if (!record) return;
    try {
      await api.patch(`/resignations/${record.id}`, {
        status: 'pending',
        reason: editReason || null,
        last_working_day: editLastDay ? editLastDay.format('YYYY-MM-DD') : null,
        application_attachments: editAttachments.length > 0 ? JSON.stringify(editAttachments) : null,
      });
      message.success('已重新提交');
      setEditModal({ open: false, record: null });
      setEditReason('');
      setEditLastDay(null);
      setEditAttachments([]);
      load();
    } catch (e) {
      const msg = e.response?.data?.message || '重新提交失败';
      message.error(msg);
    }
  };

  // 打开编辑弹窗
  const openEditModal = (record) => {
    setEditModal({ open: true, record });
    setEditReason(record.reason || '');
    setEditLastDay(record.last_working_day ? dayjs(record.last_working_day) : null);
    let attachments = [];
    try { attachments = parseAttachments(record.application_attachments); } catch {}
    setEditAttachments(attachments);
  };

  const columns = [
    { title: '工单ID', dataIndex: 'id', key: 'id', width: 80, fixed: 'left' },
    { 
      title: '员工', 
      dataIndex: 'employee_name', 
      key: 'employee_name',
      width: 120,
      fixed: 'left',
      render: (text, record) => (
        <Button 
          type="link" 
          icon={<UserOutlined />}
          onClick={() => navigate(`/employees?search=${text}`)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      )
    },
    { 
      title: '离职原因', 
      dataIndex: 'reason', 
      key: 'reason', 
      width: 250,
      ellipsis: { showTitle: true },
      render: (text) => (
        <Tooltip title={text || '-'} placement="topLeft">
          <span>{text || '-'}</span>
        </Tooltip>
      )
    },
    { 
      title: '状态', 
      dataIndex: 'status', 
      key: 'status', 
      width: 100, 
      render: status => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ) 
    },
    { 
      title: '期望最后工作日', 
      dataIndex: 'last_working_day', 
      key: 'last_working_day', 
      width: 180,
      render: (text, record) => {
        const isPending = record.status === 'pending';
        const isRejected = record.status === 'rejected';
        // 管理员可在 pending/rejected 时设定最后工作日
        const canSetDate = isAdmin && (isPending || isRejected);
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div>{text || '-'}</div>
            {canSetDate && (
              <Button 
                size="small" 
                type="link" 
                style={{ padding: '0' }} 
                onClick={() => setModal({ open: true, record, lastDay: record.last_working_day ? dayjs(record.last_working_day) : null })}
              >
                设定
              </Button>
            )}
          </div>
        );
      }
    },
    { 
      title: '创建人', 
      dataIndex: 'created_by_name', 
      key: 'created_by_name', 
      width: 100 
    },
    { 
      title: '创建时间', 
      dataIndex: 'requested_at', 
      key: 'requested_at', 
      width: 160, 
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-' 
    },
    { 
      title: '审批人', 
      dataIndex: 'approved_by_name', 
      key: 'approved_by_name', 
      width: 100 
    },
    { 
      title: '审批时间', 
      dataIndex: 'approved_at', 
      key: 'approved_at', 
      width: 160, 
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-' 
    },
    { 
      title: '操作', 
      key: 'actions', 
      width: 100, 
      fixed: 'right',
      render: (_, record) => {
        const isPending = record.status === 'pending';
        const isApproved = record.status === 'approved';
        const isRejected = record.status === 'rejected';
        const isClosed = record.status === 'closed';
        // 普通人事只能编辑自己创建的被驳回工单
        const canEditRejected = isRejected && (!isAdmin && record.created_by === user?.id);
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end', height: '100%', justifyContent: 'center' }}>
            {/* 所有状态都可以查看详情 */}
            <Button size="small" type="default" onClick={() => {
              setDetailModal({ open: true, record });
            }}>
              详情
            </Button>
            
            {/* 仅管理员可审核 */}
            {isAdmin && isPending && (
              <>
                <Button size="small" type="primary" onClick={async () => { 
                  try {
                    await api.patch(`/resignations/${record.id}`, { status: 'approved' }); 
                    message.success('已通过'); 
                    load(); 
                  } catch (error) {
                    console.error('通过离职工单失败:', error);
                    message.error('操作失败，请重试');
                  }
                }}>
                  通过
                </Button>
                <Button size="small" danger onClick={async () => { 
                  try {
                    await api.patch(`/resignations/${record.id}`, { status: 'rejected' }); 
                    message.success('已拒绝'); 
                    load(); 
                  } catch (error) {
                    console.error('拒绝离职工单失败:', error);
                    message.error('操作失败，请重试');
                  }
                }}>
                  拒绝
                </Button>
              </>
            )}
            
            {/* 仅管理员可完结 */}
            {isAdmin && isApproved && (
              <Button size="small" type="default" onClick={() => {
                setClosureModal({ open: true, record });
                closureForm.resetFields();
                setClosureAttachments([]);
              }}>
                完结
              </Button>
            )}
            
            {/* 驳回后可再次编辑并重新提交（仅创建人） */}
            {canEditRejected && (
              <Button size="small" type="primary" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
                编辑并重新提交
              </Button>
            )}
          </div>
        );
      }
    }
  ];

  // 渲染附件列表（支持图片页内预览）
  const renderAttachments = (data, errorLabel) => {
    let attachments;
    try {
      attachments = parseAttachments(data);
    } catch (e) {
      console.error(`解析${errorLabel}失败:`, e, data);
      return <span style={{ color: '#999' }}>暂无附件（数据格式错误）</span>;
    }
    
    if (attachments.length === 0) {
      return <span style={{ color: '#999' }}>暂无附件</span>;
    }
    
    return (
      <div>
        {attachments.map((file, index) => {
          const isImg = isImageFile(file);
          return (
            <div key={index} style={{ 
              marginBottom: index < attachments.length - 1 ? '12px' : '0',
              padding: '12px',
              backgroundColor: '#fff',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '20px' }}>{isImg ? '🖼️' : '📎'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{file.name}</div>
                <div style={{ color: '#999', fontSize: '12px' }}>
                  {file.size ? `大小: ${(file.size / 1024).toFixed(1)}KB` : ''}
                  {file.uploadedAt ? ` | 上传时间: ${dayjs(file.uploadedAt).format('YYYY-MM-DD HH:mm')}` : ''}
                </div>
              </div>
              {file.url && (
                isImg ? (
                  <Button size="small" type="primary" onClick={() => openImageViewer(file.url, file.name)}>
                    查看图片
                  </Button>
                ) : (
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    <Button size="small" type="primary">查看/下载</Button>
                  </a>
                )
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ padding: '10px 16px' }}>
      <Card 
        title="离职工单"
        extra={
          <Button 
            icon={<ReloadOutlined />} 
            onClick={load}
            loading={loading}
            title="刷新数据"
          >
            刷新
          </Button>
        }
      >
        <Table 
          rowKey="id" 
          loading={loading} 
          dataSource={tickets} 
          columns={columns} 
          pagination={{ pageSize: 10 }} 
          scroll={{ x: 1400, y: 'calc(100vh - 300px)' }}
          size="small"
          bordered
        />
      </Card>

      <Modal title="设定最后工作日" open={modal.open} onCancel={() => setModal({ open: false, record: null, lastDay: null })} onOk={async () => {
        try {
          await api.patch(`/resignations/${modal.record.id}`, { last_working_day: modal.lastDay ? modal.lastDay.format('YYYY-MM-DD') : null });
          message.success('已更新');
          setModal({ open: false, record: null, lastDay: null });
          load();
        } catch { message.error('更新失败'); }
      }}>
        <DatePicker style={{ width: '100%' }} value={modal.lastDay} onChange={(v) => setModal(prev => ({ ...prev, lastDay: v }))} />
      </Modal>

      {/* 结束工单模态框 */}
      <Modal 
        title="结束工单" 
        open={closureModal.open} 
        width={600}
        onCancel={() => {
          setClosureModal({ open: false, record: null });
          closureForm.resetFields();
          setClosureAttachments([]);
        }} 
        onOk={async () => {
          try {
            const values = await closureForm.validateFields();
            await api.patch(`/resignations/${closureModal.record.id}`, { 
              status: 'closed',
              closure_notes: values.notes,
              closure_attachments: JSON.stringify(closureAttachments),
              closed_at: new Date().toISOString()
            });
            message.success('工单已结束');
            setClosureModal({ open: false, record: null });
            closureForm.resetFields();
            setClosureAttachments([]);
            load();
          } catch (error) {
            console.error('结束工单失败:', error);
            message.error('操作失败，请重试');
          }
        }}
      >
        <Form form={closureForm} layout="vertical">
          <Form.Item
            label="结束备注"
            name="notes"
            rules={[{ required: true, message: '请输入结束备注' }]}
          >
            <Input.TextArea rows={4} placeholder="请输入工单结束的相关备注信息..." />
          </Form.Item>
          
          <Form.Item label="上传文件">
            <div style={{ marginBottom: '16px' }}>
              <AttachmentUpload
                value={closureAttachments}
                onChange={(files) => setClosureAttachments(files)}
                compact={false}
                showQrButton={true}
                autoCreateFollowUp={false}
                hideList={false}
                attachToLead={false}
              />
            </div>
            
            <div style={{ color: '#999', fontSize: '12px', lineHeight: '1.4' }}>
              💡 支持上传离职薪资确认表、交接清单、工作交接文档等相关文件<br />
              📱 移动端支持拍照上传，建议使用高清拍照模式
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑/重新提交模态框 */}
      <Modal
        title="编辑并重新提交离职工单"
        open={editModal.open}
        width={800}
        onCancel={() => {
          setEditModal({ open: false, record: null });
          setEditReason('');
          setEditLastDay(null);
          setEditAttachments([]);
        }}
        onOk={handleResubmit}
        okText="重新提交"
        cancelText="取消"
      >
        <div style={{ display: 'flex', gap: '24px', minHeight: '400px' }}>
          {/* 左侧：表单信息 */}
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 'bold' }}>最后工作日</div>
              <DatePicker style={{ width: '100%' }} value={editLastDay} onChange={setEditLastDay} />
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 'bold' }}>离职原因</div>
              <Input.TextArea 
                rows={6} 
                value={editReason} 
                onChange={e => setEditReason(e.target.value)} 
                placeholder="请输入离职原因（可选）" 
              />
            </div>
          </div>
          
          {/* 右侧：上传组件 */}
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{ marginBottom: 8, fontWeight: 'bold' }}>上传离职申请单</div>
            <AttachmentUpload
              value={editAttachments}
              onChange={setEditAttachments}
              compact={false}
              showQrButton={true}
              attachToLead={false}
            />
          </div>
        </div>
      </Modal>

      {/* 查看工单详情模态框 */}
      <Modal 
        title="离职工单详情" 
        open={detailModal.open} 
        width={900}
        onCancel={() => setDetailModal({ open: false, record: null })}
        footer={[
          <Button key="close" onClick={() => setDetailModal({ open: false, record: null })}>
            关闭
          </Button>
        ]}
      >
        {detailModal.record && (
          <div>
            {/* 基本信息 */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ 
                borderLeft: '4px solid #1890ff', 
                paddingLeft: '12px',
                marginBottom: '16px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>基本信息</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', paddingLeft: '16px' }}>
                <div><strong>工单ID：</strong>{detailModal.record.id}</div>
                <div><strong>员工姓名：</strong>{detailModal.record.employee_name}</div>
                <div><strong>状态：</strong><Tag color={getStatusColor(detailModal.record.status)}>{getStatusText(detailModal.record.status)}</Tag></div>
                <div><strong>最后工作日：</strong>{detailModal.record.last_working_day || '-'}</div>
                <div><strong>创建人：</strong>{detailModal.record.created_by_name || '-'}</div>
                <div><strong>创建时间：</strong>{detailModal.record.requested_at ? dayjs(detailModal.record.requested_at).format('YYYY-MM-DD HH:mm') : '-'}</div>
                <div><strong>审批人：</strong>{detailModal.record.approved_by_name || '-'}</div>
                <div><strong>审批时间：</strong>{detailModal.record.approved_at ? dayjs(detailModal.record.approved_at).format('YYYY-MM-DD HH:mm') : '-'}</div>
              </div>
            </div>

            {/* 离职原因 */}
            {detailModal.record.reason && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ 
                  borderLeft: '4px solid #52c41a', 
                  paddingLeft: '12px',
                  marginBottom: '16px',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}>离职原因</h4>
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: '#f6ffed', 
                  border: '1px solid #b7eb8f',
                  borderRadius: '6px',
                  whiteSpace: 'pre-wrap',
                  marginLeft: '16px'
                }}>
                  {detailModal.record.reason}
                </div>
              </div>
            )}

            {/* 申请附件 */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ 
                borderLeft: '4px solid #faad14', 
                paddingLeft: '12px',
                marginBottom: '16px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>申请附件</h4>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#fffbe6', 
                border: '1px solid #ffe58f',
                borderRadius: '6px',
                marginLeft: '16px'
              }}>
                {renderAttachments(detailModal.record.application_attachments, '申请附件')}
              </div>
            </div>

            {/* 结束备注 */}
            {detailModal.record.closure_notes && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ 
                  borderLeft: '4px solid #722ed1', 
                  paddingLeft: '12px',
                  marginBottom: '16px',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}>结束备注</h4>
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: '#f9f0ff', 
                  border: '1px solid #d3adf7',
                  borderRadius: '6px',
                  whiteSpace: 'pre-wrap',
                  marginLeft: '16px'
                }}>
                  {detailModal.record.closure_notes}
                </div>
              </div>
            )}

            {/* 结束附件 */}
            {detailModal.record.status === 'closed' && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ 
                  borderLeft: '4px solid #13c2c2', 
                  paddingLeft: '12px',
                  marginBottom: '16px',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}>结束附件</h4>
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: '#e6fffb', 
                  border: '1px solid #87e8de',
                  borderRadius: '6px',
                  marginLeft: '16px'
                }}>
                  {renderAttachments(detailModal.record.closure_attachments, '结束附件')}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 图片查看器 */}
      <ImageViewer
        visible={imageViewerVisible}
        onClose={closeImageViewer}
        imageUrl={currentImage.url}
        imageName={currentImage.name}
        downloadUrl={currentImage.downloadUrl}
      />
    </div>
  );
};

export default Resignations;
